import { FC } from "react";
import {
    AccountsIcon,
    AccountingIcon,
    BillPayIcon,
    ChartOfAccountsIcon,
    ClockIcon,
    CogwheelIcon,
    DirectoryIcon,
    FolderIcon,
    CashFlowIcon,
    GrowthIcon,
    InvoicingIcon,
    LedgerIcon,
    ReconciledIcon,
    RulesIcon,
    SpreadsheetIcon,
    CategoryIcon,
    TransactionsIcon,
    ClassIcon,
    TemplatesIcon,
    WorkIcon,
} from "@icons";

export type NavigationGate = "hasBusinessEntities" | "notGlFirst";

interface NavigationShortcutBase {
    id: string;
    shortcut?: string[];
    label: string;
    icon?: FC;
    description: string;
    gate?: NavigationGate | NavigationGate[];
}

export type NavigationShortcutDefinition =
    | (NavigationShortcutBase & { path: string; action?: never })
    | (NavigationShortcutBase & { path?: never; action: "settings" });

export function shortcutToKeys(
    shortcut: string[] | undefined,
): string | undefined {
    return shortcut?.map((k) => k.toLowerCase()).join(">");
}

export const NAVIGATION_SHORTCUTS: NavigationShortcutDefinition[] = [
    {
        id: "tasks",
        shortcut: ["G", "Q"],
        path: "/",
        label: "Go to Actions",
        icon: WorkIcon,
        description: "Navigate to Actions page",
    },
    {
        id: "activity",
        shortcut: ["G", "Y"],
        path: "/activity",
        label: "Go to Activity",
        icon: ClockIcon,
        description: "Navigate to Activity page",
    },
    {
        id: "insights",
        shortcut: ["G", "W"],
        path: "/insights",
        label: "Go to Insights",
        icon: CashFlowIcon,
        description: "Navigate to Insights page",
        gate: "notGlFirst",
    },
    {
        id: "transactions",
        shortcut: ["G", "T"],
        path: "/transactions",
        label: "Go to Transactions",
        icon: TransactionsIcon,
        description: "Navigate to Transactions page",
    },
    {
        id: "documents",
        shortcut: ["G", "D"],
        path: "/documents",
        label: "Go to Documents",
        icon: FolderIcon,
        description: "Navigate to Documents page",
    },
    {
        id: "accounts",
        shortcut: ["G", "A"],
        path: "/accounts",
        label: "Go to Accounts",
        icon: AccountsIcon,
        description: "Navigate to Accounts page",
    },
    {
        id: "invoicing",
        shortcut: ["G", "I"],
        path: "/invoicing",
        label: "Go to Invoicing",
        icon: InvoicingIcon,
        description: "Navigate to Invoicing page",
    },
    {
        id: "rules",
        shortcut: ["G", "R"],
        path: "/rules",
        label: "Go to Rules",
        icon: RulesIcon,
        description: "Navigate to Rules page",
    },
    {
        id: "categories",
        shortcut: ["G", "C"],
        path: "/categories",
        label: "Go to Categories",
        icon: CategoryIcon,
        description: "Navigate to Categories page",
        gate: "notGlFirst",
    },
    {
        id: "classes",
        shortcut: ["G", "F"],
        path: "/classes",
        label: "Go to Classes",
        icon: ClassIcon,
        description: "Navigate to Classes page",
    },
    {
        id: "billing",
        shortcut: ["G", "B"],
        path: "/billing",
        label: "Go to Bill pay",
        icon: BillPayIcon,
        description: "Navigate to Bill pay page",
    },
    {
        id: "accounting",
        shortcut: ["G", "M"],
        path: "/accounting",
        label: "Go to Reporting",
        icon: AccountingIcon,
        description: "Navigate to Reporting page",
        gate: "hasBusinessEntities",
    },
    {
        id: "chart-of-accounts",
        shortcut: ["G", "H"],
        path: "/chart-of-accounts",
        label: "Go to Chart of Accounts",
        icon: ChartOfAccountsIcon,
        description: "Navigate to Chart of Accounts page",
        gate: "hasBusinessEntities",
    },
    {
        id: "reconciliation",
        shortcut: ["G", "J"],
        path: "/reconciliation",
        label: "Go to Reconciliation",
        icon: ReconciledIcon,
        description: "Navigate to Reconciliation page",
        gate: "hasBusinessEntities",
    },
    {
        id: "counterparties",
        shortcut: ["G", "V"],
        path: "/counterparties",
        label: "Go to Counterparties",
        icon: DirectoryIcon,
        description: "Navigate to Counterparties page",
        gate: "hasBusinessEntities",
    },
    {
        id: "profit-loss",
        shortcut: ["G", "K"],
        path: "/accounting/profit-loss",
        label: "Go to Profit & Loss",
        icon: GrowthIcon,
        description: "Navigate to Profit & Loss page",
        gate: "hasBusinessEntities",
    },
    {
        id: "balance-sheet",
        shortcut: ["G", "L"],
        path: "/accounting/balance-sheet",
        label: "Go to Balance Sheet",
        icon: SpreadsheetIcon,
        description: "Navigate to Balance Sheet page",
        gate: "hasBusinessEntities",
    },
    {
        id: "general-ledger",
        shortcut: ["G", "G"],
        path: "/accounting/general-ledger",
        label: "Go to General Ledger",
        icon: LedgerIcon,
        description: "Navigate to General Ledger page",
        gate: "hasBusinessEntities",
    },
    {
        id: "trial-balance",
        shortcut: ["G", "N"],
        path: "/accounting/trial-balance",
        label: "Go to Trial Balance",
        icon: LedgerIcon,
        description: "Navigate to Trial Balance page",
        gate: "hasBusinessEntities",
    },
    {
        id: "catalog-items",
        path: "/invoicing/items",
        label: "Go to Catalog Items",
        icon: InvoicingIcon,
        description: "Navigate to Catalog Items page",
    },
    {
        id: "invoice-templates",
        path: "/invoicing/templates",
        label: "Go to Invoice Templates",
        icon: TemplatesIcon,
        description: "Navigate to Invoice Templates page",
    },
    {
        id: "ar-aging-summary",
        path: "/accounting/ar-aging-summary",
        label: "Go to AR Aging Summary",
        icon: SpreadsheetIcon,
        description: "Navigate to AR Aging Summary page",
        gate: "hasBusinessEntities",
    },
    {
        id: "ar-aging-detail",
        path: "/accounting/ar-aging-detail",
        label: "Go to AR Aging Detail",
        icon: SpreadsheetIcon,
        description: "Navigate to AR Aging Detail page",
        gate: "hasBusinessEntities",
    },
    {
        id: "settings",
        shortcut: ["G", "S"],
        label: "Go to Settings",
        icon: CogwheelIcon,
        description: "Navigate to Settings",
        action: "settings",
    },
];
